import React, { useEffect, useState } from "react";
import { api, fmtEUR, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Globe2, CalendarDays, Users, Luggage, MapPin, CheckCircle2, XCircle, Package } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import BookingModal from "../components/BookingModal";

const TABS = [["Da confermare", "Da confermare"], ["Convertita", "Convertite"], ["Rifiutata", "Rifiutate"], ["", "Tutte"]];
const STATUS_CLS = { "Da confermare": "text-amber-300 border-amber-500/40", Convertita: "text-emerald-300 border-emerald-500/40", Rifiutata: "text-rose-300 border-rose-500/40" };

const toBookingDraft = (r) => ({
  service_type: r.package?.name || "Tourvoyagers",
  date: r.date,
  time: r.time || "09:00",
  passengers: r.passengers || 1,
  luggage: r.luggage || 0,
  passenger_name: r.customer?.name || "",
  phone: r.customer?.phone || "",
  email: r.customer?.email || "",
  pickup: r.itinerary?.pickup || "",
  destination: r.itinerary?.destination || "",
  stops: r.itinerary?.stops || "",
  price: r.price || 0,
  notes: [`Richiesta Tourvoyagers · Rif. ${r.external_id}`, r.package?.name && `Pacchetto: ${r.package.name}${r.package.code ? ` (${r.package.code})` : ""}`, r.notes].filter(Boolean).join("\n"),
});

function RequestCard({ r, onConvert, onReject }) {
  return (
    <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-b from-[#121009] to-[#070605] p-4 space-y-2" data-testid={`tv-request-${r.external_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-white font-semibold truncate">{r.customer?.name || "Cliente non indicato"}</div>
          <div className="text-[11px] text-slate-500 truncate">Rif. {r.external_id} · ricevuta {new Date(r.created_at).toLocaleString("it-IT")}</div>
        </div>
        <span className={`shrink-0 text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${STATUS_CLS[r.status] || ""}`} data-testid="tv-request-status">{r.status}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-300">
        <div className="flex items-center gap-2"><Package size={14} className="text-amber-400" />{r.package?.name || "—"}{r.package?.code ? ` (${r.package.code})` : ""}</div>
        <div className="flex items-center gap-2"><CalendarDays size={14} className="text-amber-400" />{r.date?.split("-").reverse().join("/")} {r.time || ""}</div>
        <div className="flex items-start gap-2 sm:col-span-2"><MapPin size={14} className="text-amber-400 mt-0.5" /><span>{r.itinerary?.pickup || "—"} → {r.itinerary?.destination || "—"}{r.itinerary?.stops ? ` · tappe: ${r.itinerary.stops}` : ""}</span></div>
        <div className="flex items-center gap-4"><span className="inline-flex items-center gap-1"><Users size={14} className="text-amber-400" />{r.passengers ?? "—"} pax</span><span className="inline-flex items-center gap-1"><Luggage size={14} className="text-amber-400" />{r.luggage ?? "—"} bagagli</span></div>
        <div className="text-amber-300 font-display">{r.price != null ? fmtEUR(r.price) : "Prezzo non indicato"}</div>
      </div>
      {(r.customer?.email || r.customer?.phone) && <div className="text-xs text-slate-400">{[r.customer.email, r.customer.phone].filter(Boolean).join(" · ")}</div>}
      {r.notes && <div className="text-xs text-slate-300 whitespace-pre-wrap border-t border-slate-800 pt-2">{r.notes}</div>}
      {r.status === "Convertita" && <div className="text-xs text-emerald-300">Prenotazione {r.booking_number}</div>}
      {r.status === "Rifiutata" && r.reject_reason && <div className="text-xs text-rose-300">Motivo: {r.reject_reason}</div>}
      {r.status === "Da confermare" && (
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={() => onConvert(r)} data-testid={`tv-convert-${r.external_id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-sm font-semibold bg-gradient-to-b from-[#F1D27A] to-[#C99A24] text-black"><CheckCircle2 size={15} />Converti in prenotazione</button>
          <button type="button" onClick={() => onReject(r)} data-testid={`tv-reject-${r.external_id}`} className="inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm border border-rose-500/40 text-rose-300"><XCircle size={15} />Rifiuta</button>
        </div>
      )}
    </div>
  );
}

export default function RichiesteTourvoyagers() {
  const [tab, setTab] = useState("Da confermare");
  const [rows, setRows] = useState([]);
  const [converting, setConverting] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => api.get("/external-requests", { params: tab ? { status: tab } : {} }).then(r => setRows(r.data || [])).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const afterBooking = async (saved) => {
    const req = converting;
    setConverting(null);
    if (!saved || saved === true || !saved.id) return;
    try {
      await api.post(`/external-requests/${req.id}/mark-converted`, { booking_id: saved.id });
      toast.success(`Richiesta convertita in ${saved.booking_number || "prenotazione"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    load();
  };
  const doReject = async () => {
    try {
      await api.post(`/external-requests/${rejecting.id}/reject`, { reason });
      toast.success("Richiesta rifiutata");
      setRejecting(null); setReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="tv-requests-page">
      <div>
        <h1 className="font-display text-3xl text-white flex items-center gap-2"><Globe2 className="text-amber-400" />Richieste Tourvoyagers</h1>
        <p className="text-sm text-slate-400 mt-1">Richieste ricevute dal sito Tourvoyagers. Diventano prenotazioni solo dopo la tua conferma.</p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {TABS.map(([k, label]) => (
          <button key={label} type="button" onClick={() => setTab(k)} data-testid={`tv-tab-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={`px-3 py-1.5 rounded-full text-xs border ${tab === k ? "bg-amber-500/15 border-amber-400 text-amber-200" : "border-slate-700 text-slate-400"}`}>{label}</button>
        ))}
      </div>
      <div className="space-y-3">
        {rows.length === 0 && <div className="text-sm text-slate-500 text-center py-10" data-testid="tv-empty">Nessuna richiesta</div>}
        {rows.map(r => <RequestCard key={r.id} r={r} onConvert={setConverting} onReject={setRejecting} />)}
      </div>
      {converting && <BookingModal booking={toBookingDraft(converting)} onClose={afterBooking} />}
      {rejecting && (
        <Dialog open onOpenChange={(o) => !o && setRejecting(null)}>
          <DialogContent className="bg-[#0A0907] border-amber-500/30 text-white" data-testid="tv-reject-dialog">
            <DialogTitle className="font-display">Rifiuta richiesta {rejecting.external_id}</DialogTitle>
            <Textarea placeholder="Motivo (facoltativo, visibile a Tourvoyagers)" value={reason} maxLength={500} onChange={e => setReason(e.target.value)} className="bg-black/60 border-amber-500/25" data-testid="tv-reject-reason" />
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setRejecting(null)} className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 text-sm">Annulla</button>
              <button type="button" onClick={doReject} className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold" data-testid="tv-reject-confirm">Conferma rifiuto</button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
